import React, { useState } from 'react';

// Profile Options Helper (could be imported but keeping self-contained for UI)
const LEVELS = [
    { id: 'pro', label: 'Pro / NFL' },
    { id: 'college', label: 'College' },
    { id: 'hs', label: 'High School' },
    { id: 'peewee', label: 'Youth / Pee-Wee' },
    { id: 'flag_adult', label: 'Flag: Adult' },
    { id: 'flag_13plus', label: 'Flag: 13+' },
    { id: 'flag_12u', label: 'Flag: 12U' },
    { id: 'flag_10u', label: 'Flag: 10U' },
];

const FORMATS = [
    { id: '11v11', label: '11 vs 11' },
    { id: '7v7', label: '7 vs 7' },
    { id: '6v6', label: '6 vs 6' },
    { id: '5v5', label: '5 vs 5' },
];

const PlaybookWizard = ({ onClose, onCreate }) => {
    const [step, setStep] = useState(1);
    const [data, setData] = useState({
        name: '',
        level: 'flag_adult',
        format: '5v5',
        labelMode: 'user'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const update = (k, v) => setData(prev => ({ ...prev, [k]: v }));

    const handleFinish = async () => {
        setIsSubmitting(true);
        try {
            await onCreate({
                name: data.name,
                level: data.level,
                format: data.format,
                labelMode: data.labelMode
            });
        } catch (e) {
            alert(e.message);
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-700 w-full max-w-md rounded-2xl p-6 shadow-2xl flex flex-col">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold">New Playbook</h2>
                    <div className="flex gap-1">
                        {[1, 2].map(i => (
                            <div key={i} className={`h-1.5 w-6 rounded-full transition-colors ${step >= i ? 'bg-blue-500' : 'bg-gray-700'}`} />
                        ))}
                    </div>
                </div>

                <div className="flex-1 min-h-[200px]">
                    {step === 1 && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                            <label className="block text-sm font-bold text-gray-400">Playbook Name</label>
                            <input
                                autoFocus
                                value={data.name}
                                onChange={e => update('name', e.target.value)}
                                className="w-full bg-gray-800 border border-gray-600 rounded-lg p-3 text-lg focus:border-blue-500 outline-none"
                                placeholder="e.g. Fall Season 2024"
                                onKeyDown={e => e.key === 'Enter' && data.name && setStep(2)}
                            />
                            <p className="text-xs text-gray-500">Give your playbook a recognizable name.</p>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div>
                                <label className="block text-sm font-bold text-gray-400 mb-2">Level of Play</label>
                                <select
                                    value={data.level}
                                    onChange={e => update('level', e.target.value)}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg p-3 outline-none focus:border-blue-500"
                                >
                                    {LEVELS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-400 mb-2">Format</label>
                                <select
                                    value={data.format}
                                    onChange={e => update('format', e.target.value)}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg p-3 outline-none focus:border-blue-500"
                                >
                                    {FORMATS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                                </select>
                            </div>
                            <div className="bg-blue-900/20 border border-blue-500/20 p-3 rounded-lg text-xs list-disc border-l-4 border-l-blue-500">
                                <p className="font-bold text-blue-400 mb-1">Profile Adjustments:</p>
                                <ul className="list-disc pl-4 text-gray-400 space-y-1">
                                    <li>Scales routes for field size</li>
                                    <li>Adjusts player speeds</li>
                                    <li>Sets throw velocity/arc defaults</li>
                                </ul>
                            </div>
                        </div>
                    )}

                </div>

                <div className="flex justify-between mt-6 pt-4 border-t border-gray-800">
                    <button
                        onClick={step === 1 ? onClose : () => setStep(step - 1)}
                        className="px-4 py-2 text-gray-400 hover:text-white"
                        disabled={isSubmitting}
                    >
                        {step === 1 ? 'Cancel' : 'Back'}
                    </button>

                    {step < 2 ? (
                        <button
                            onClick={() => setStep(step + 1)}
                            disabled={!data.name}
                            className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed px-6 py-2 rounded-lg font-bold text-white transition-colors"
                        >
                            Next
                        </button>
                    ) : (
                        <button
                            onClick={handleFinish}
                            disabled={isSubmitting}
                            className="bg-green-600 hover:bg-green-500 disabled:opacity-50 px-6 py-2 rounded-lg font-bold text-white transition-colors flex items-center gap-2"
                        >
                            {isSubmitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                            Create Playbook
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PlaybookWizard;
